import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  Typography,
  Box,
  IconButton,
  Card,
  CardContent,
  Button,
  Grow,
  Divider,
  useTheme,
  Avatar,
  Tooltip,
  LinearProgress,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  DialogActions,
  Grid,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InventoryIcon from '@mui/icons-material/Inventory';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import DescriptionIcon from '@mui/icons-material/Description';
import ImageIcon from '@mui/icons-material/Image';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { StockNotification } from '../types/notifications';
import MirakelParticles from './MirakelParticles';

const safePercent = (value: number, total: number) => {
  if (!total || total <= 0) {
    return 0;
  }

  return Math.min(100, Math.max(0, (value / total) * 100));
};

interface StockNotificationsDialogProps {
  open: boolean;
  onClose: () => void;
  notifications: StockNotification[];
  onMarkAsRead: (notificationId: number) => Promise<void>;
  onOpenProduct: (notification: StockNotification) => void;
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
  onPageChange: (newPage: number) => void;
  filters: {
    brand: string;
    hasDescription: string;
    hasAllColorImages: string;
  };
  onFilterChange: (filterName: string, value: string) => void;
  brands: Array<{
    brandName: string;
    unreadCount: number;
  }>;
  counts: {
    totalUnread: number;
    missingDescription: number;
    missingColorImages: number;
    completeAndUnread: number;
    totalBrands: number;
  };
  onMarkCompleteAsRead: () => Promise<void>;
  isBulkMarking: boolean;
}

const StockNotificationsDialog: React.FC<StockNotificationsDialogProps> = ({
  open,
  onClose,
  notifications,
  onMarkAsRead,
  onOpenProduct,
  pagination,
  onPageChange,
  filters,
  onFilterChange,
  brands,
  counts,
  onMarkCompleteAsRead,
  isBulkMarking,
}) => {
  const theme = useTheme();
  const [confirmBulkOpen, setConfirmBulkOpen] = useState(false);

  const handleConfirmBulkMark = async () => {
    await onMarkCompleteAsRead();
    setConfirmBulkOpen(false);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 18px 60px rgba(15, 23, 42, 0.18)',
          overflow: 'hidden',
          minHeight: '80vh',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 3,
          display: 'flex',
          alignItems: 'center',
          borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
          position: 'relative',
          overflow: 'hidden',
          '& > :not(.mirakel-particles)': {
            position: 'relative',
            zIndex: 1,
          },
        }}
      >
        <MirakelParticles
          variant="corner"
          density="low"
          opacity={0.28}
          sx={{ display: { xs: 'none', sm: 'block' } }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <Avatar
            sx={{
              bgcolor: 'primary.main',
              width: 40,
              height: 40,
              mr: 2,
            }}
          >
            <InventoryIcon sx={{ fontSize: 24 }} />
          </Avatar>
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
              }}
            >
              Lagerförändringar
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Totalt {pagination.total} notifieringar
            </Typography>
          </Box>
        </Box>
        <IconButton
          aria-label="stäng"
          onClick={onClose}
          sx={{
            color: theme.palette.text.secondary,
            transition: 'color 160ms ease, background-color 160ms ease',
            '&:hover': {
              color: theme.palette.primary.main,
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        <Box
          sx={{
            mb: 3,
            p: 2,
            backgroundColor: '#F8FAFC',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Varumärke</InputLabel>
                <Select
                  value={filters.brand}
                  onChange={(e) => onFilterChange('brand', e.target.value)}
                  label="Varumärke"
                >
                  <MenuItem value="">Alla varumärken</MenuItem>
                  {brands.map((brand) => (
                    <MenuItem key={brand.brandName} value={brand.brandName}>
                      {brand.brandName} ({brand.unreadCount})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Beskrivningsstatus</InputLabel>
                <Select
                  value={filters.hasDescription}
                  onChange={(e) =>
                    onFilterChange('hasDescription', e.target.value)
                  }
                  label="Beskrivningsstatus"
                >
                  <MenuItem value="">Alla</MenuItem>
                  <MenuItem value="true">Med beskrivning</MenuItem>
                  <MenuItem value="false">Utan beskrivning</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Färgbildsstatus</InputLabel>
                <Select
                  value={filters.hasAllColorImages}
                  onChange={(e) =>
                    onFilterChange('hasAllColorImages', e.target.value)
                  }
                  label="Färgbildsstatus"
                >
                  <MenuItem value="">Alla</MenuItem>
                  <MenuItem value="true">Alla färger har bilder</MenuItem>
                  <MenuItem value="false">Vissa färger saknar bilder</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {counts.completeAndUnread > 0 && (
            <Box
              sx={{
                mt: 2,
                pt: 2,
                borderTop: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {counts.completeAndUnread} kompletta notifieringar
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Produkter med giltig beskrivning och alla färgbilder
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircleIcon />}
                disabled={isBulkMarking}
                onClick={() => setConfirmBulkOpen(true)}
                sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                {isBulkMarking
                  ? 'Markerar...'
                  : 'Markera alla kompletta som lästa'}
              </Button>
            </Box>
          )}
        </Box>

        {notifications.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 6,
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Avatar
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                width: 80,
                height: 80,
                mb: 2,
              }}
            >
              <InventoryIcon
                sx={{ fontSize: 48, color: theme.palette.primary.main }}
              />
            </Avatar>
            <Typography variant="h6" color="text.secondary" fontWeight={500}>
              Inga nya lagerförändringar
            </Typography>
          </Box>
        ) : (
          <>
            <List sx={{ p: 0 }}>
              {notifications.map((notification) => (
                <Grow key={notification.notificationId} in={true} timeout={200}>
                  <Card
                    sx={{
                      mb: 2,
                      backgroundColor: 'background.paper',
                      transition:
                        'box-shadow 160ms ease, border-color 160ms ease',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      boxShadow: '0 1px 2px rgba(15, 23, 42, 0.05)',
                      overflow: 'hidden',
                      position: 'relative',
                      '&:hover': {
                        borderColor: '#CBD5E1',
                        boxShadow: '0 4px 14px rgba(15, 23, 42, 0.08)',
                      },
                    }}
                  >
                    <CardContent sx={{ position: 'relative', p: 3 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: { xs: 'column', sm: 'row' },
                          alignItems: 'flex-start',
                          justifyContent: 'space-between',
                          gap: 2,
                        }}
                      >
                        <Box>
                          <Typography
                            variant="h6"
                            gutterBottom
                            sx={{
                              fontWeight: 600,
                              color: theme.palette.text.primary,
                            }}
                          >
                            {notification.brandName} -{' '}
                            {notification.productName}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: theme.palette.text.secondary,
                              mb: 2,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                          >
                            Art.nr: {notification.productNumber}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: 1,
                            flexShrink: 0,
                            width: { xs: '100%', sm: 'auto' },
                          }}
                        >
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<OpenInNewIcon />}
                            onClick={() => onOpenProduct(notification)}
                            sx={{
                              whiteSpace: 'nowrap',
                              width: { xs: '100%', sm: 'auto' },
                            }}
                          >
                            Öppna produkt
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<CheckCircleIcon />}
                            onClick={() =>
                              onMarkAsRead(notification.notificationId)
                            }
                            sx={{
                              whiteSpace: 'nowrap',
                              width: { xs: '100%', sm: 'auto' },
                            }}
                          >
                            Markera som läst
                          </Button>
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 3,
                          backgroundColor: '#F8FAFC',
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          p: 2,
                          mt: 2,
                        }}
                      >
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            Tidigare:
                          </Typography>
                          <Typography
                            variant="h6"
                            color="text.primary"
                            fontWeight={600}
                          >
                            {notification.previousBalance} st
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            color: notification.increase
                              ? theme.palette.success.main
                              : theme.palette.error.main,
                            gap: 1,
                            px: 2,
                            py: 0.5,
                            borderRadius: '8px',
                            bgcolor: notification.increase
                              ? alpha(theme.palette.success.main, 0.1)
                              : alpha(theme.palette.error.main, 0.1),
                          }}
                        >
                          {notification.increase ? (
                            <TrendingUpIcon />
                          ) : (
                            <TrendingDownIcon />
                          )}
                          <Typography variant="h6" fontWeight={600}>
                            {notification.increase ? '+' : ''}
                            {notification.newBalance -
                              notification.previousBalance}{' '}
                            st
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 2,
                            py: 0.5,
                            borderRadius: '8px',
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="primary.main"
                            fontWeight={500}
                          >
                            Nytt saldo:
                          </Typography>
                          <Typography
                            variant="h6"
                            color="primary.main"
                            fontWeight={600}
                          >
                            {notification.newBalance} st
                          </Typography>
                        </Box>
                      </Box>

                      {/* New Product Information Status Section */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          mt: 2,
                          p: 2,
                          backgroundColor: '#F8FAFC',
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <Tooltip
                          title={
                            notification.hasValidDescription
                              ? 'Produkten har en giltig beskrivning'
                              : 'Produkten behöver en bättre beskrivning'
                          }
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <DescriptionIcon
                              color={
                                notification.hasValidDescription
                                  ? 'success'
                                  : 'warning'
                              }
                            />
                            <Typography
                              variant="body2"
                              color={
                                notification.hasValidDescription
                                  ? 'success.main'
                                  : 'warning.main'
                              }
                            >
                              Beskrivning
                            </Typography>
                          </Box>
                        </Tooltip>

                        <Divider orientation="vertical" flexItem />

                        <Tooltip
                          title={`${notification.colorsWithImages} av ${notification.totalColors} färger har bilder`}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              minWidth: '120px',
                            }}
                          >
                            <ImageIcon color="primary" />
                            <Box sx={{ flex: 1, ml: 1 }}>
                              <LinearProgress
                                variant="determinate"
                                value={safePercent(
                                  notification.colorsWithImages,
                                  notification.totalColors,
                                )}
                                sx={{
                                  height: 8,
                                  borderRadius: 4,
                                  backgroundColor: '#E2E8F0',
                                  '& .MuiLinearProgress-bar': {
                                    borderRadius: 4,
                                    background: (theme) => {
                                      const percentage =
                                        safePercent(
                                          notification.colorsWithImages,
                                          notification.totalColors,
                                        ) / 100;
                                      if (percentage === 1)
                                        return theme.palette.success.main;
                                      if (percentage >= 0.75)
                                        return theme.palette.success.light;
                                      if (percentage >= 0.5)
                                        return theme.palette.warning.main;
                                      return theme.palette.error.main;
                                    },
                                  },
                                }}
                              />
                            </Box>
                            <Typography
                              variant="body2"
                              color="primary.main"
                              sx={{ ml: 1 }}
                            >
                              {notification.colorsWithImages}/
                              {notification.totalColors}
                            </Typography>
                          </Box>
                        </Tooltip>
                      </Box>

                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          mt: 2,
                          color: theme.palette.text.secondary,
                          fontStyle: 'italic',
                        }}
                      >
                        {new Date(notification.createdAt).toLocaleDateString(
                          'sv-SE',
                          {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          },
                        )}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grow>
              ))}
            </List>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={pagination.totalPages}
                page={pagination.page}
                onChange={(_, page) => onPageChange(page)}
                color="primary"
                size="large"
                sx={{
                  '& .MuiPaginationItem-root': {
                    borderRadius: 1,
                    margin: '0 4px',
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: '#FFFFFF',
                    },
                  },
                }}
              />
            </Box>
          </>
        )}
      </DialogContent>

      <Dialog
        open={confirmBulkOpen}
        onClose={() => !isBulkMarking && setConfirmBulkOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Markera kompletta notifieringar?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Detta markerar {counts.completeAndUnread} notifieringar med giltig
            beskrivning och alla färgbilder som lästa. Åtgärden kan inte ångras.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setConfirmBulkOpen(false)}
            disabled={isBulkMarking}
          >
            Avbryt
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleConfirmBulkMark}
            disabled={isBulkMarking}
            startIcon={<CheckCircleIcon />}
          >
            {isBulkMarking ? 'Markerar...' : 'Markera som lästa'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default StockNotificationsDialog;
